/**
 * External dependencies
 */
import React, { memo, useMemo, useRef } from 'react';

/**
 * Internal dependencies
 */
import { ImageProps } from './types';

function Image(props: ImageProps): React.ReactElement {
  /* Global & Local State */
  const { className, src, typeCircular, typeBorder, typeSize, typeAsCover, customErrorImage, nativeProps } = props;
  const imageRefs = useRef<HTMLImageElement>(null);

  /* Functions */
  const onError = (event: React.SyntheticEvent<HTMLImageElement, Event>): void => {
    let errorImage = '';
    if (customErrorImage) {
      errorImage = customErrorImage;
    } else {
      errorImage = '';
    }
    event.currentTarget.onerror = null;
    event.currentTarget.src = errorImage;
  };

  /* Views */
  const renderCircular: React.CSSProperties = useMemo(() => {
    if (typeCircular) {
      return { borderRadius: '100px' };
    }
    return {};
  }, [typeCircular]);
  const renderBorder: React.CSSProperties = useMemo(() => {
    if (typeBorder) {
      return { border: '4px solid #6E5BFD' };
    }
    return {};
  }, [typeBorder]);
  const renderSize: React.CSSProperties = useMemo(() => {
    switch (typeSize) {
      case 'tiny':
        return { width: '15px', height: 'auto' };
      case 'small':
        return { width: '20px', height: 'auto' };
      case 'medium':
        return {
          width: '23px',
          height: 'auto'
        };
      case 'large':
        return {
          width: '74px',
          height: 'auto'
        };
      case 'fit':
        return {
          width: '100%',
          height: '100%'
        };
      default:
        return {
          width: '100%',
          height: '100%'
        };
    }
  }, [typeSize]);
  const renderCover: React.CSSProperties = useMemo(() => {
    if (typeAsCover) {
      return { objectFit: 'cover' };
    }
    return { objectFit: 'contain' };
  }, [typeAsCover]);
  const renderSrc: string = useMemo(() => {
    let imageSrc = '';
    if (src) {
      return src;
    }
    if (nativeProps?.src) {
      return nativeProps?.src;
    }
    imageSrc = '';
    return imageSrc;
  }, [nativeProps, src]);

  return (
    <React.Fragment>
      <img
        className={className}
        style={{ ...nativeProps?.style, ...renderBorder, ...renderCircular, ...renderSize, ...renderCover }}
        onError={onError}
        ref={imageRefs}
        alt="custom"
        src={renderSrc}
        loading="lazy"
      />
    </React.Fragment>
  );
}

export default memo(Image);
